/**
 * Converters between the persisted document shape (QdaDocument.paragraphs —
 * option A in the migration plan: document.json stays plain-text paragraphs)
 * and a ProseMirror document. Inline marks live only in the editor session;
 * the persisted/bridge contract remains plain text.
 */

import { Node as PMNode, Schema } from "@tiptap/pm/model";
import { Paragraph } from "../types";
import { newId } from "../utils/idGenerator";

export interface DefaultBlockFont {
  fontFamily?: string;
  fontSize?: string;
}

/**
 * `defaultFont` is the caller's job to resolve (Settings > General's default
 * font family/size) — this module is foundational plumbing imported by
 * nearly every editor/docx/collab test, and settingsStore transitively
 * imports settings/registry.ts, whose require.context() call only works
 * under webpack, not Jest, so this file must not import it directly.
 */
export function paragraphsToPmDoc(paragraphs: Paragraph[], schema: Schema, defaultFont?: DefaultBlockFont): PMNode {
  const blocks = [...paragraphs]
    .sort((a, b) => a.index - b.index)
    .map((para) => {
      const paraId = para.id || newId();
      const content = para.text ? [schema.text(para.text)] : [];
      const m = /^Heading([1-3])$/.exec(para.style ?? "");
      if (m) return schema.node("heading", { paraId, level: Number(m[1]) }, content);
      return schema.node("paragraph", { paraId }, content);
    });
  // A ProseMirror doc requires at least one block. Only a genuinely-empty
  // document (no persisted paragraphs at all — i.e. brand new) gets the
  // default font/size seeded onto it; existing content is untouched.
  if (blocks.length === 0) {
    const attrs: Record<string, unknown> = { paraId: newId() };
    if (defaultFont?.fontFamily) attrs.defaultFontFamily = defaultFont.fontFamily;
    if (defaultFont?.fontSize) attrs.defaultFontSize = defaultFont.fontSize;
    blocks.push(schema.node("paragraph", attrs));
  }
  return schema.node("doc", null, blocks);
}

export function pmDocToParagraphs(doc: PMNode): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  doc.forEach((node, _offset, index) => {
    paragraphs.push({
      id: node.attrs.paraId ?? newId(),
      index,
      text: node.textContent,
      style: node.type.name === "heading" ? `Heading${node.attrs.level}` : "Normal",
    });
  });
  return paragraphs;
}
